import * as React from 'react';
import { Card, Steps, Divider, Button } from 'antd';
import { IconName } from '@fortawesome/fontawesome-common-types';
import horizontalStepperCss from './horizontal-stepper.css';
import { showConfirmDialog } from '../dialogs/confirm-dialog';
import cx from 'classnames';

export type StepAction = () => void | Promise<void>;

export interface IStepNavigationConfig {
    next?: {
        hidden?: boolean;
        disabled?: boolean;
        before?: () => unknown | Promise<unknown>;
        confirm?: (action: StepAction) => unknown;
    };
    back?: {
        before?: () => unknown;
    };
}

export interface IHorizontalStepperActionsConfig {
    hidden?: boolean;
    canGoBack?: boolean;
    confirmBack?: boolean;
    cancel?: {
        label?: string;
        onClick?: () => void;
    };
    finish?: {
        stepCode?: string;
        label?: string;
        hidden?: boolean;
        disabled?: boolean;
        onClick?: () => void | Promise<void>;
    };
}

export interface IStepItem {
    code: string;
    className?: string;
    iconName?: IconName;
    label: string | React.ReactNode;
    description?: string | React.ReactNode | any[];
    content?: React.ReactNode;
    navigation?: IStepNavigationConfig;
    status?: 'wait' | 'process' | 'finish' | 'error';
}

export interface IHorizontalStepperProps {
    items: IStepItem[];
    currentStepCode?: string;
    onStepCodeChange?: (previous: IStepItem, current: IStepItem) => void;
    className?: string;
    actions?: IHorizontalStepperActionsConfig;
}

function confirm(action?: StepAction) {
    showConfirmDialog({
        title: 'Confirm',
        content: 'Are you sure you want to navigate back? Data will be lost.',
        okText: 'OK',
        cancelText: 'Cancel',
        onOk: () => action?.(),
    });
}

export function HorizontalStepper(props: IHorizontalStepperProps) {
    const [currentStepCode, setCurrentStepCode] = React.useState(props.currentStepCode || props.items[0].code);
    const currentStepIndex = Math.max(props.items.findIndex((item) => item.code === currentStepCode), 0);
    const currentItem = props.items[currentStepIndex];
    const finishStepCode = props.actions?.finish?.stepCode || props.items[props.items.length - 1]?.code;

    React.useEffect(() => {
        if (props.currentStepCode) {
            setCurrentStepCode(props.currentStepCode);
        }
    }, [props.currentStepCode]);

    const onGoBack = () => {
        currentItem.navigation?.back?.before?.();

        const previousStepItem = props.items[currentStepIndex - 1];
        if (!previousStepItem) return;

        setCurrentStepCode(previousStepItem.code);
        props.onStepCodeChange?.(currentItem, previousStepItem);
    };

    const onNext = async () => {
        if (currentItem.navigation?.next?.before) {
            await currentItem.navigation.next.before();
        }

        const nextStepItem = props.items[currentStepIndex + 1];
        if (!nextStepItem) return;

        setCurrentStepCode(nextStepItem.code);
        props.onStepCodeChange?.(currentItem, nextStepItem);
    };

    const NavigationBar = (props: IHorizontalStepperActionsConfig) => {
        const { hidden, canGoBack, cancel, finish, confirmBack } = props;
        if (hidden) {
            return <React.Fragment></React.Fragment>;
        }
        return (
            <React.Fragment>
                <Divider />
                <div className={horizontalStepperCss.actions}>
                    {canGoBack && currentStepIndex > 0 ? (
                        <Button type='default' onClick={() => (confirmBack ? confirm(onGoBack) : onGoBack())}>
                            BACK
                        </Button>
                    ) : cancel?.onClick && canGoBack && currentStepIndex === 0 ? (
                        <Button type='default' onClick={() => confirm(cancel?.onClick)}>
                            {cancel.label || 'CANCEL'}
                        </Button>
                    ) : (
                        <div />
                    )}
                    {currentItem.code === finishStepCode ? (
                        !finish?.hidden && (
                            <Button
                                htmlType='submit'
                                type='primary'
                                disabled={finish?.disabled}
                                onClick={finish?.onClick}
                            >
                                {finish?.label || 'FINISH'}
                            </Button>
                        )
                    ) : !currentItem.navigation?.next?.hidden ? (
                        <Button
                            type='primary'
                            disabled={currentItem.navigation?.next?.disabled}
                            onClick={
                                !currentItem.navigation?.next?.confirm
                                    ? onNext
                                    : () => currentItem.navigation.next.confirm(onNext)
                            }
                        >
                            NEXT
                        </Button>
                    ) : (
                        <div></div>
                    )}
                </div>
            </React.Fragment>
        );
    };

    return (
        <Card className={cx(horizontalStepperCss.horizontalStepper, props.className)}>
            <Steps size='small' type='navigation' current={currentStepIndex}>
                {props.items.map((stepItem) => (
                    <Steps.Step
                        key={stepItem.code}
                        title={`${stepItem.label.toString().toUpperCase()}`}
                        status='wait'
                        description={stepItem.description}
                    />
                ))}
            </Steps>
            <div className={horizontalStepperCss.stepContent}>
                {props.items[currentStepIndex].content ? props.items[currentStepIndex].content : <div />}
            </div>
            <NavigationBar {...(props.actions ?? {})} />
            {/* {!props.actions?.hidden && (
                <React.Fragment>
                    <Divider />
                    <div className={horizontalStepperCss.actions}>
                        {props.actions?.canGoBack && currentStepIndex > 0 ? (
                            <Button type='default' onClick={() => (props.actions?.confirmBack ? confirm(onGoBack) : onGoBack())}>
                                BACK
                            </Button>
                        ) : props.actions?.cancel?.onClick && props.actions?.canGoBack && currentStepIndex === 0 ? (
                            <Button type='default' onClick={() => confirm(props.actions?.cancel?.onClick)}>
                                {props.actions.cancel.label || 'CANCEL'}
                            </Button>
                        ) : (
                            <div />
                        )}
                        {currentItem.code === finishStepCode ? (
                            !props.actions?.finish?.hidden && (
                                <Button
                                    htmlType='submit'
                                    type='primary'
                                    disabled={props.actions?.finish?.disabled}
                                    onClick={props.actions?.finish?.onClick}
                                >
                                    {props.actions?.finish?.label || 'FINISH'}
                                </Button>
                            )
                        ) : !currentItem.navigation?.next?.hidden ? (
                            <Button
                                type='primary'
                                disabled={currentItem.navigation?.next?.disabled}
                                onClick={
                                    !currentItem.navigation?.next?.confirm
                                        ? onNext
                                        : () => currentItem.navigation.next.confirm(onNext)
                                }
                            >
                                NEXT
                            </Button>
                        ) : (
                            <div></div>
                        )}
                    </div>
                </React.Fragment>
            )} */}
        </Card>
    );
}
